// The Client → Contact CRM field map (SEP-177, ADR-0025 D1).
//
// ADR-0025 D1 decided that once Contacts go live, **`Contact` is authoritative
// for person and vulnerability data** and `Client`'s Dynamics descriptive block
// is FROZEN — no longer edited in-app, rendered read-only with a cross-link to
// the matched Contact. Two very different consumers need to agree on exactly
// which fields that covers:
//
//   - the one-off migration script (`apps/server/scripts/migrate-client-crm-to-contact.ts`),
//     which copies each mapped value onto the matched Contact through the
//     `UpdateContact` Action; and
//   - the Client Book detail page, which renders exactly those fields locked
//     with the "maintained on the Contact" notice.
//
// One list, in the shared package, so the page can never freeze a field the
// migration does not move (data stranded, editable nowhere) nor leave editable
// a field the migration silently overwrites on its next run.
//
// The join key is `ontology_clients.dynamics_contact_id` ↔
// `contacts.crm_contact_id` — both are written from the SAME Dynamics export
// column, `(Do Not Modify) Contact` (`dynamics-contacts.service.ts:491`,
// `contacts-backfill.service.ts:127`). Measured against prod on 2026-07-30:
// `dynamics_contact_id` populated on 2,380 / 4,644 clients, `crm_contact_id` on
// 2,076 / 2,076 contacts, and **1,752 clients match a contact**.
// `ontology_clients.tercero_client_ref` is populated on ZERO rows and is NOT
// the key, despite the ticket's "trading account refs → GPP Client ID / ref"
// wording.
//
// `contactField` is a plain string here because `packages/contract` may not
// import the ontology packages, and `clientField` is a `ClientIdentity` (DTO)
// key rather than a `Client` Object Type key. NEITHER side is checked by the
// compiler, so both are pinned at test time by
// `apps/server/src/lib/identity/__tests__/crm-to-contact-plan.test.ts` — the
// server is the only place that may import the contract AND the ontology. It
// fails the build if a mapping names a `contactField` that `UpdateContact`
// will not accept or that is outside `CONTACT_DESCRIPTIVE_KEYS` (a provenance
// column written as though it were person data), or a `clientField` the
// `Client` Object Type does not carry (the migration would read `undefined`,
// report `source_empty` and silently move nothing, while the page padlocks the
// field anyway — data stranded with no editable home).

import type { ClientIdentity } from './contracts/client-book';

export interface ClientCrmFieldMapping {
    /** The `ontology_clients` column / `ClientIdentity` key the value comes from. */
    clientField: keyof ClientIdentity;
    /** The `contacts` column / `UpdateContact` input key it lands on. */
    contactField: string;
    /** Human label, used by the migration report and the frozen-field notice. */
    label: string;
}

/**
 * Every Client CRM field that has a Contact home. Order is the order the
 * migration report prints and reads roughly like the record form: name,
 * personal, address, contact channels, employment, vulnerability.
 */
export const CLIENT_CRM_FIELD_MAP: readonly ClientCrmFieldMapping[] = [
    { clientField: 'title', contactField: 'title', label: 'Title' },
    { clientField: 'firstName', contactField: 'firstName', label: 'First name' },
    { clientField: 'middleNames', contactField: 'middleNames', label: 'Middle name(s)' },
    { clientField: 'lastName', contactField: 'lastName', label: 'Last name' },
    { clientField: 'dateOfBirth', contactField: 'dateOfBirth', label: 'Date of birth' },
    { clientField: 'gender', contactField: 'gender', label: 'Gender' },
    { clientField: 'maritalStatus', contactField: 'maritalStatus', label: 'Marital status' },
    { clientField: 'nationality', contactField: 'nationality', label: 'Nationality' },
    { clientField: 'addressLine1', contactField: 'addressLine1', label: 'Address line 1' },
    { clientField: 'addressLine2', contactField: 'addressLine2', label: 'Address line 2' },
    { clientField: 'city', contactField: 'city', label: 'City' },
    { clientField: 'county', contactField: 'county', label: 'County' },
    { clientField: 'postcode', contactField: 'postcode', label: 'Postcode' },
    { clientField: 'country', contactField: 'country', label: 'Country' },
    { clientField: 'email', contactField: 'email', label: 'Email' },
    { clientField: 'secondaryEmail', contactField: 'secondaryEmail', label: 'Secondary email' },
    // `Client.phone` is the mobile number and `Contact.phone` IS the Mobile
    // column — there is no `mobile_phone` anywhere (measured; see the SEP-174
    // decision). `Client.mainPhone` has no Contact home at all.
    { clientField: 'phone', contactField: 'phone', label: 'Mobile phone' },
    { clientField: 'homePhone', contactField: 'homePhone', label: 'Home phone' },
    { clientField: 'workNumber', contactField: 'workNumber', label: 'Work number' },
    { clientField: 'employer', contactField: 'employer', label: 'Employer' },
    { clientField: 'occupation', contactField: 'occupation', label: 'Occupation' },
    {
        clientField: 'preferredMethodOfContact',
        contactField: 'preferredMethodOfContact',
        label: 'Preferred method of contact',
    },
    { clientField: 'vulnerable', contactField: 'vulnerable', label: 'Vulnerable?' },
    { clientField: 'vulnerableNotes', contactField: 'vulnerableNotes', label: 'Vulnerable notes' },
];

/**
 * The Client identity keys the map covers — i.e. the fields the Client record
 * freezes, because the Contact is now authoritative for them (ADR-0025 D1).
 */
export const CLIENT_FROZEN_CRM_FIELDS: ReadonlySet<keyof ClientIdentity> = new Set(
    CLIENT_CRM_FIELD_MAP.map((m) => m.clientField),
);

export function isFrozenClientCrmField(field: keyof ClientIdentity): boolean {
    return CLIENT_FROZEN_CRM_FIELDS.has(field);
}

/**
 * Client CRM fields with NO Contact column to move to. They stay editable on
 * the Client record because there is nowhere else to edit them — naming them
 * here is the honest half of "the CRM block moves to the Contact".
 *
 * Giving each of these a Contact home is a follow-up that costs a `contacts`
 * migration plus Object Type / Action / contract / UI changes per field; it is
 * deliberately not folded into this pass.
 */
export const CLIENT_CRM_FIELDS_WITHOUT_CONTACT_HOME: readonly (keyof ClientIdentity)[] = [
    'paEmail',
    'mainPhone',
    'personalNotes',
    'anniversary',
    'spousePartnerName',
    'spousePartnerContactId',
    'staffAccount',
    'typeOfContact',
    'corporateRetail',
    'fcaClassification',
    'nidn',
];

/**
 * Contact descriptive fields with no Client source, listed so the migration's
 * silence about them is deliberate rather than an omission:
 *
 *   - `countryOfResidence` — `ontology_clients` has an address `country` only,
 *     which is a different question and must not be copied across;
 *   - `inceptionDate` — `Client.clientSince` is the CLIENT tier's inception
 *     (when the account relationship began), not the Contact record's.
 */
export const CONTACT_FIELDS_WITH_NO_CLIENT_SOURCE: readonly string[] = [
    'countryOfResidence',
    'inceptionDate',
];
